import { sortBy } from "lodash";
import React, { useContext, useState } from "react";
import styled from "styled-components";

import { parseClassroom } from "@examspark/api";
import {
  formatStudentName,
  getTagValueFromResource,
  reactKeyFromIDSK,
  SystemTag,
} from "@examspark/common";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  HSpace,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconAddLine,
  IconDownloadLine,
  IconPencilLine,
  IconRefreshLine,
  IconTrashLine,
  IconUploadLine,
} from "@examspark/common-ui/dist/components/icons";
import { Classroom, Student } from "@examspark/types";

import {
  useMutationExportClassroomRoster,
  useQueryGetClassroomStudents,
} from "../../api/operations/classroom";
import {
  useMutationDeleteStudent,
  useMutationUpdateStudent,
} from "../../api/operations/student";
import { useModal } from "../../hooks/useModal";
import { useAppContext } from "../../providers/AppContext";
import { LTIContext } from "../../providers/LTIProvider";
import SetupPrompt from "../SetupPrompt";

import CreateStudentModal from "./student/CreateStudentModal";
import EditStudentModal from "./student/EditStudentModal";
import UpdateRosterModal from "./student/UpdateRosterModal";

const StudentsTable = styled.table`
  width: 100%;
  table-layout: fixed;

  thead tr th {
    font-weight: normal;
    text-align: left;
    font-size: 1.4rem;
    color: #555;
  }
  td {
    border-top: solid 1px #ddd;
  }
  td,
  th {
    padding: 0.5rem 0.5rem;
  }
  tbody tr {
    transition: background-color 0.2s ease;
  }
  tbody tr:hover {
    background-color: #f7f7f7;
  }
`;

const StudentListItemName = styled.td``;
const StudentListItemId = styled.td``;

const StudentsListItem = styled.tr<Pick<Student, "isDeleted">>`
  ${(props) =>
    props.isDeleted &&
    `
    ${StudentListItemName}, ${StudentListItemId} {
      text-decoration: line-through;
    }
  `}
`;

const ClassroomRoster = (props: {
  classroom: Classroom;
  disabled?: boolean;
  link?: (student: Student) => React.ReactNode;
}) => {
  const lti = useContext(LTIContext);
  const [state] = useAppContext();
  const classroom = parseClassroom(props.classroom);
  const classroomId = props.classroom.id;
  const {
    data: students,
    isFetching: isFetchingStudents,
    refetch: refetchStudents,
  } = useQueryGetClassroomStudents(state.workspaceUser?.id, classroomId);
  const [filter, setFilter] = useState("");
  const [isCreateStudentModalOpen, setIsCreateStudentModalOpen] =
    useState(false);
  const updateRosterModal = useModal();
  const deleteStudent = useMutationDeleteStudent();
  const updateStudent = useMutationUpdateStudent();
  const exportRoster = useMutationExportClassroomRoster();
  const [editingStudent, setEditingStudent] = useState<Student>();
  const [isExportingRoster, setIsExportingRoster] = useState(false);
  const isManaged = !!lti?.token.iss;

  const handleExportClassroomRoster = async () => {
    if (!state.workspaceUser?.id) return;
    if (!classroomId) return;

    try {
      setIsExportingRoster(true);
      const result = await exportRoster.mutateAsync({
        workspaceId: state.workspaceUser?.id,
        classroomId: classroomId,
      });

      setIsExportingRoster(false);
      if (result) {
        const _result = JSON.parse(result);
        window.location.href = _result.file;
      }
    } catch (error) {
      setIsExportingRoster(false);
    }
  };

  // todo: user configurable
  const sortedStudents = sortBy(students, ["lastName", "firstName"]);

  return (
    <>
      <UpdateRosterModal
        {...updateRosterModal.modalProps}
        classroom={classroom}
        onComplete={() => {
          updateRosterModal.setIsOpen(false);
          refetchStudents();
        }}
      />
      <CreateStudentModal
        isOpen={isCreateStudentModalOpen}
        onRequestClose={() => setIsCreateStudentModalOpen(false)}
        classroom={classroom}
        onCreateStudent={() => {
          setIsCreateStudentModalOpen(false);
          refetchStudents();
        }}
      />
      <EditStudentModal
        isOpen={!!editingStudent}
        onRequestClose={() => setEditingStudent(undefined)}
        onUpdate={() => {
          refetchStudents();
          setEditingStudent(undefined);
        }}
        classroom={classroom}
        student={editingStudent}
      />
      <VSpace>
        <Toolbar>
          <TextInput
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter Students by Name or ID"
            style={{ flex: 1 }}
            value={filter}
          />
          <Button
            title="Reload"
            icon={<IconRefreshLine />}
            isLoading={isFetchingStudents}
            onClick={() => {
              refetchStudents();
            }}
          />
          <Button
            title="Download Roster"
            icon={<IconDownloadLine />}
            onClick={handleExportClassroomRoster}
            isLoading={isExportingRoster}
            disabled={isExportingRoster}
          >
            Download
          </Button>
          <Button
            title="Upload Roster"
            disabled={props.disabled}
            icon={<IconUploadLine />}
            onClick={() => updateRosterModal.setIsOpen(true)}
          >
            Upload
          </Button>
          <Button
            title="New Student"
            disabled={props.disabled || isManaged}
            color="create"
            icon={<IconAddLine />}
            onClick={() => setIsCreateStudentModalOpen(true)}
          >
            Student
          </Button>
        </Toolbar>
        {students?.length === 0 ? (
          <>
            <SetupPrompt>
              <HSpace size="3rem">
                <div>
                  <svg
                    id="Layer_1"
                    enable-background="new 0 0 511.993 511.993"
                    viewBox="0 0 511.993 511.993"
                    width="64"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <g>
                        <g>
                          <g>
                            <g>
                              <path
                                d="m452.2 276.649h-364.502c-15.411 0-27.904-12.494-27.904-27.907 0-15.413 12.493-27.907 27.904-27.907h364.502c-8.964 17.356-8.964 38.457 0 55.814z"
                                fill="#f7f7f7"
                              />
                              <path
                                d="m452.2 220.834h-364.502 334.502c-8.964 17.357-8.964 38.458 0 55.814h30c-8.964-17.356-8.964-38.457 0-55.814z"
                                fill="#efefef"
                              />
                              <path
                                d="m91.762 299.892c-13.736 0-26.637-5.389-36.327-15.173-9.687-9.783-14.949-22.738-14.816-36.48.271-27.927 23.467-50.647 51.707-50.647h363.939c8.333 0 15.112 6.78 15.112 15.113s-6.779 15.113-15.112 15.113h-364.503c-11.536 0-20.921 9.386-20.921 20.924s9.385 20.924 20.921 20.924h364.502c8.333 0 15.112 6.78 15.112 15.113s-6.779 15.113-15.112 15.113z"
                                fill="#ff815c"
                              />
                            </g>
                            <g>
                              <g>
                                <path
                                  d="m486.265 481.25h-424.503c-15.411 0-27.904-12.494-27.904-27.907 0-15.413 12.493-27.907 27.904-27.907h424.502c-8.964 17.356-8.964 38.457.001 55.814z"
                                  fill="#f7f7f7"
                                />
                                <path
                                  d="m486.265 425.436h-30c-8.964 17.357-8.964 38.457 0 55.814h30c-8.965-17.357-8.965-38.458 0-55.814z"
                                  fill="#efefef"
                                />
                                <path
                                  d="m61.762 504.493c-13.736 0-26.637-5.389-36.327-15.173-9.687-9.783-14.949-22.738-14.816-36.48.271-27.927 23.467-50.647 51.707-50.647h423.939c8.333 0 15.112 6.78 15.112 15.113s-6.779 15.113-15.112 15.113h-424.503c-11.536 0-20.921 9.386-20.921 20.924s9.385 20.924 20.921 20.924h424.502c8.333 0 15.112 6.78 15.112 15.114 0 8.333-6.779 15.113-15.112 15.113h-424.502z"
                                  fill="#ffe66b"
                                />
                              </g>
                              <g>
                                <path
                                  d="m25.729 323.135h424.502c15.411 0 27.904 12.494 27.904 27.907 0 15.413-12.493 27.907-27.904 27.907h-424.502c8.964-17.357 8.964-38.457 0-55.814z"
                                  fill="#f7f7f7"
                                />
                                <path
                                  d="m25.729 402.192c-8.333 0-15.112-6.78-15.112-15.113s6.779-15.113 15.112-15.113h424.502c11.536 0 20.922-9.386 20.922-20.924s-9.386-20.924-20.922-20.924h-424.502c-8.333 0-15.112-6.78-15.112-15.113s6.779-15.113 15.112-15.113h424.503c13.736 0 26.637 5.389 36.326 15.173 9.688 9.784 14.948 22.739 14.816 36.48-.271 27.927-23.467 50.647-51.707 50.647z"
                                  fill="#c9f0ff"
                                />
                                <path
                                  d="m486.558 315.065c-9.689-9.784-22.59-15.173-36.326-15.173h-30c13.736 0 26.637 5.389 36.326 15.173 9.688 9.784 14.948 22.739 14.816 36.48-.271 27.927-23.467 50.647-51.707 50.647h30c28.24 0 51.436-22.72 51.707-50.647.132-13.742-5.129-26.697-14.816-36.48z"
                                  fill="#b5eaff"
                                />
                              </g>
                            </g>
                          </g>
                          <g>
                            <path
                              d="m395.997 197.591h-280l30-101.009h220z"
                              fill="#288a9a"
                            />
                            <path
                              d="m230.14 10.832-199.009 52.272c-10.668 2.802-10.668 17.947 0 20.749l199.009 52.272c16.95 4.452 34.763 4.452 51.713 0l199.009-52.272c10.668-2.802 10.668-17.947 0-20.749l-199.009-52.272c-16.95-4.452-34.763-4.452-51.713 0z"
                              fill="#3498ac"
                            />
                            <path
                              d="m480.862 63.104-199.009-52.272c-16.95-4.452-34.763-4.452-51.713 0l-30.239 7.943c13.939-2.232 28.228-1.547 41.952 2.057l199.009 52.272c10.668 2.802 10.668 17.947 0 20.749l40-10c10.669-2.802 10.669-17.947 0-20.749z"
                              fill="#288a9a"
                            />
                            <path
                              d="m345.465 240.993h-43.164s0-65.138 21.582-65.138c21.582 0 21.582 65.138 21.582 65.138z"
                              fill="#ffe66b"
                            />
                          </g>
                        </g>
                        <path
                          d="m128.67 432.419-35.673 18.716v-121.017h35.673z"
                          fill="#3498ac"
                        />
                      </g>
                      <path
                        d="m270.093 77.074c-9.327 5.712-20.348 5.719-29.656 0v-21.319c0-8.189 6.639-14.828 14.828-14.828 8.189 0 14.828 6.639 14.828 14.828z"
                        fill="#ffe66b"
                      />
                      <path d="m488.769 466.908c-2.311-8.885-2.309-18.258 0-27.131 26.584-3.64 26.684-41.12.15-44.924 12.086-10.641 19.789-26.081 19.955-43.237.219-22.843-13.357-44.277-34.105-53.864 10.775-15.323-1.613-36.7-20.101-35.588-2.259-8.784-2.262-18.052 0-26.848 29.885 1.565 31.85-45.227 1.598-45.227h-54.672l-21.397-72.042 102.572-26.942c17.684-4.645 17.708-30.605 0-35.257l-199.01-52.27c-18.162-4.771-37.361-4.771-55.523 0l-74 19.438c-9.078 2.384-5.681 16.997 3.811 14.508l74-19.438c15.669-4.115 32.233-4.115 47.902 0l199.009 52.272c3.203.841 3.202 5.4 0 6.241l-153.158 40.229-48.206-43.71v-17.363c-1.226-29.62-43.433-29.613-44.656 0v21.319c0 2.606 1.353 5.026 3.574 6.39 10.156 6.235 22.066 7.117 32.843 2.431l39.129 35.48-28.536 7.495c-15.669 4.115-32.233 4.115-47.902 0l-199.009-52.271c-3.203-.842-3.204-5.4 0-6.241l87.056-22.866c9.078-2.384 5.68-17-3.811-14.508l-87.056 22.866c-17.684 4.645-17.709 30.605 0 35.257l102.571 26.941-21.397 72.042h-18.074c-32.335 0-58.896 26.052-59.207 58.075-.162 16.764 7.051 33.021 19.506 44.226h-26.896c-28.506.977-30.562 41.054-2.502 45.084 2.314 8.885 2.314 18.246 0 27.131-26.585 3.638-26.686 41.12-.152 44.924-12.086 10.641-19.789 26.08-19.956 43.236-.152 15.76 5.881 30.615 16.987 41.831 11.109 11.218 25.903 17.396 41.656 17.396h424.502c28.415-.935 30.645-41.015 2.505-45.085zm-226.176-394.443c-4.792 1.871-9.876 1.874-14.656.005v-16.715c.345-9.7 14.314-9.694 14.656 0zm187.638 265.153c17.782.726 17.824 26.103 0 26.848h-314.06v-26.848zm-329.061 90.266-20.673 10.846v-101.112h20.673zm-35.673-63.418h-46.855c1.759-8.856 1.759-17.992 0-26.848h46.855zm3.619 93.086c2.435 1.364 4.89 1.438 7.365.224l34.037-17.857h342.836c-1.754 8.849-1.753 17.991.002 26.848h-411.594c-7.4 0-13.421-6.022-13.421-13.424s6.021-13.424 13.421-13.424h23.734v11.216c.001 2.625 1.374 5.059 3.62 6.417zm350.173-195.387h-347.527c-7.4 0-13.421-6.022-13.421-13.424s6.021-13.424 13.421-13.424h203.16c-.189 5.342-.161 13.175 7.378 13.175h43.164c7.562 0 7.567-7.83 7.378-13.175h86.449c-1.755 8.858-1.756 17.999-.002 26.848zm-115.406-78.81c6.609 0 12.9 23.453 13.934 50.138h-27.868c1.033-26.685 7.325-50.138 13.934-50.138zm139.994 29.349c0 4.198-3.415 7.613-7.612 7.613h-104.407c-.493-5.099-1.149-10.185-2.059-15.227h106.466c4.197.001 7.612 3.417 7.612 7.614zm-77.931-22.613h-39.894c-3.569-10.504-8.474-17.221-14.669-20.105v-39.116l34.299-9.009zm-239.634-68.23 81.923 21.518c18.093 4.752 37.431 4.752 55.523 0l32.624-8.569v35.176c-6.196 2.884-11.1 9.601-14.669 20.105h-175.665zm-85.548 157.58c-8.268-8.349-12.759-19.405-12.646-31.13.232-23.832 20.063-43.22 44.208-43.22h205.64c-.91 5.042-1.566 10.128-2.059 15.227h-204.145c-15.672 0-28.421 12.751-28.421 28.424s12.75 28.424 28.421 28.424h364.502c9.523 0 9.959 15.227 0 15.227h-364.502c-11.718-.001-22.727-4.6-30.998-12.952zm-42.647 35.564c0-4.198 3.415-7.613 7.612-7.613h424.503c23.929 0 43.872 20.14 43.643 44.08-.232 23.832-20.063 43.22-44.208 43.22h-39.337c-9.385 0-9.813 15 0 15h75.935c4.197 0 7.612 3.415 7.612 7.613s-3.415 7.613-7.612 7.613h-350.095v-15.227h234.632c9.385 0 9.813-15 0-15h-234.632v-15.227h314.061c15.672 0 28.422-12.751 28.422-28.424s-12.75-28.424-28.422-28.424h-424.502c-4.198.002-7.612-3.414-7.612-7.611zm7.612 79.687c-10.077-.359-10.069-14.871 0-15.227h59.768v15.227zm460.536 102.301h-424.503c-11.719 0-22.728-4.599-30.998-12.951-8.268-8.35-12.759-19.405-12.646-31.13.232-23.832 20.063-43.22 44.208-43.22h23.171v15.227h-23.735c-37.714 1.566-37.685 55.296 0 56.848h424.502c10.078.358 10.071 14.871.001 15.226z" />
                    </g>
                  </svg>
                </div>
                <div>
                  <VSpace size="1rem">
                    <SetupPrompt.Title>
                      You haven't rostered your classroom yet.
                    </SetupPrompt.Title>
                    <SetupPrompt.Text>
                      To get started, add students to your classroom either
                      manually or by uploading a roster. You can also download a
                      roster template by clicking the button below, filling it
                      out, and uploading it.
                    </SetupPrompt.Text>
                    <div>
                      <Button
                        title="Download Roster Template"
                        icon={<IconDownloadLine />}
                        variant="outlined"
                        onClick={handleExportClassroomRoster}
                      >
                        Download Roster Template
                      </Button>
                    </div>
                  </VSpace>
                </div>
              </HSpace>
            </SetupPrompt>
          </>
        ) : (
          <>
            <div>
              <StudentsTable cellPadding={0} cellSpacing={0}>
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}>Name</th>
                    <th>Student ID</th>
                    <th>Access Code</th>
                    <th style={{ width: "10rem", textAlign: "center" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents?.map((student) => {
                    const filterableName =
                      formatStudentName(student) + `${student.studentId}`;
                    if (
                      filterableName &&
                      filter &&
                      !filterableName
                        .toLowerCase()
                        .includes(filter.toLowerCase())
                    ) {
                      return null;
                    }

                    const accessCode = getTagValueFromResource(
                      student,
                      SystemTag.StudentAssignmentAccessCode,
                    );

                    return (
                      <StudentsListItem
                        key={reactKeyFromIDSK(student)}
                        isDeleted={student.isDeleted}
                      >
                        <StudentListItemName>
                          {props.link ? props.link(student) : student.fullName}
                        </StudentListItemName>
                        <StudentListItemId>
                          {student.studentId}
                        </StudentListItemId>
                        <StudentListItemId>{accessCode}</StudentListItemId>
                        <td
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          {student.isDeleted && (
                            <Toolbar>
                              <Button
                                title="Restore"
                                disabled={props.disabled}
                                variant="outlined"
                                onClick={async () => {
                                  await ConfirmModal.show({
                                    onResolving: async (result) => {
                                      if (result) {
                                        await updateStudent.mutateAsync({
                                          id: student.id,
                                          sk: student.sk,
                                          isDeleted: false,
                                          workspaceId: student.workspaceId,
                                          schemaVersion: student.schemaVersion,
                                        });
                                        refetchStudents();
                                      }
                                    },
                                    children: (
                                      <CautionBanner variant="plain">
                                        Are you sure you want to restore this
                                        student?
                                      </CautionBanner>
                                    ),
                                  });
                                }}
                              >
                                Restore
                              </Button>
                            </Toolbar>
                          )}
                          {!student.isDeleted && (
                            <Toolbar>
                              <Button
                                title="Edit"
                                disabled={props.disabled}
                                icon={<IconPencilLine />}
                                onClick={() => setEditingStudent(student)}
                              />
                              <Button
                                title="Delete"
                                disabled={props.disabled}
                                icon={<IconTrashLine />}
                                onClick={async () => {
                                  await ConfirmModal.show({
                                    onResolving: async (result) => {
                                      if (result) {
                                        await deleteStudent.mutateAsync({
                                          id: student.id,
                                          sk: student.sk,
                                        });
                                        refetchStudents();
                                      }
                                    },
                                    children: (
                                      <CautionBanner variant="plain">
                                        Are you sure you want to delete this
                                        student? We will still preserve the
                                        student's record so that reports
                                        containing data from this student is not
                                        lost.
                                      </CautionBanner>
                                    ),
                                  });
                                }}
                              />
                            </Toolbar>
                          )}
                        </td>
                      </StudentsListItem>
                    );
                  })}
                </tbody>
              </StudentsTable>
            </div>
          </>
        )}
      </VSpace>
    </>
  );
};

export default ClassroomRoster;
